import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Bird from '../components/FlappyBird/Bird';
import Pipes from '../components/FlappyBird/Pipes';

// Wave animation for "Flappy Bird"
function WaveText({ 
  children, 
  amplitude = 40, 
  speed = 8, 
  pause = 1,      // Pause duration in seconds
  bounces = 4,    // Number of bounces per cycle
  decay = 0.4,    // Amplitude decay factor (0.5 = halves each bounce)
  delayPerLetter = 0.05, 
  fontSize = '48px' 
}) {
  const [time, setTime] = useState(0);

  useEffect(() => {
    let frameId;
    const animate = () => {
      setTime(prev => prev + 0.01);
      frameId = requestAnimationFrame(animate);
    };
    animate();
    return () => cancelAnimationFrame(frameId);
  }, []);

  return (
    <div style={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
      {children.split('').map((char, i) => {
        const delay = i * delayPerLetter;
        const localTime = time - delay;
        
        // Calculate total cycle duration (bounces + pause)
        const cycleDuration = (Math.PI * bounces) / speed + pause;
        const cycleTime = localTime % cycleDuration;
        
        let y = 0;
        let activeBounce = -1;
        
        // Check if we're in a bounce phase
        for (let bounce = 0; bounce < bounces; bounce++) {
          const bounceStart = (Math.PI * bounce) / speed;
          const bounceEnd = (Math.PI * (bounce + 1)) / speed;
          
          if (cycleTime >= bounceStart && cycleTime < bounceEnd) {
            activeBounce = bounce;
            break;
          }
        }
        
        // Calculate bounce position if active
        if (activeBounce > -1) {
          const bounceTime = (cycleTime - (Math.PI * activeBounce) / speed) * speed;
          const bounceAmplitude = amplitude * Math.pow(decay, activeBounce);
          y = bounceAmplitude * Math.sin(bounceTime);
        }
        
        return (
          <span
            key={i}
            className="outlined-text"
            style={{
              display: 'inline-block',
              transform: `translateY(${-y}px)`,
              fontSize: fontSize,
              fontFamily: "'Sniglet', cursive",
              fontWeight: 800,
            }}
          >
            {char}
          </span>
        );
      })}
    </div>
  );
}

// Pulsing text for "Tap to Fly" with smooth opacity and enlargement animation
function PulseText({ children, speed = 1 }) {
  const [time, setTime] = useState(0);

  useEffect(() => {
    let frameId;
    const animate = () => {
      setTime(prev => prev + 0.02 * speed);
      frameId = requestAnimationFrame(animate);
    };
    animate();
    return () => cancelAnimationFrame(frameId);
  }, [speed]);

  // Opacity oscillates between 0.5 and 1
  const opacity = 0.75 + 0.25 * Math.sin(time * Math.PI);
  // Scale oscillates between 1 and 1.05 (or adjust as desired)
  const scale = 1 + 0.05 * Math.sin(time * Math.PI);

  return (
    <div style={{
      opacity,
      transform: `scale(${scale})`,
      fontSize: '24px',
      fontFamily: "'Sniglet', sans-serif",
      fontWeight: 400,
      color: '#000',
      display: 'inline-block',
    }}>
      {children}
    </div>
  );
}

// Adding a browser size listener for dynamic browser resizing (with debouncing)
function useWindowSize() {
  
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    let timeoutId;
    const handleResize = () => {
      // Debounce resize events to reduce excessive recalculations
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setWindowSize({
          width: window.innerWidth,
          height: window.innerHeight,
        });
      }, 100); // 100ms debounce
    };
    window.addEventListener('resize', handleResize);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return windowSize;
}

function hexToRgb(hex) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return { r, g, b };
}

function rgbToHex(r, g, b) {
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}

function interpolateColor(color1, color2, t) {
  const c1 = hexToRgb(color1);
  const c2 = hexToRgb(color2);
  const r = Math.round(c1.r + (c2.r - c1.r) * t);
  const g = Math.round(c1.g + (c2.g - c1.g) * t);
  const b = Math.round(c1.b + (c2.b - c1.b) * t);
  return rgbToHex(r, g, b);
}

// Pre-defining the farm background images
const FARM_BG_IMAGES = [
  '/flappy_elements/farm-green.png',
  '/flappy_elements/farm-blue.png',
  '/flappy_elements/farm-purple.png'
];


// Main function
function FlappyBird() {
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);

  // CRITICAL: Preload all images immediately on component mount (before any other state)
  useEffect(() => {
    const imagesToPreload = [
      '/flappy_elements/farm-green.png',
      '/flappy_elements/farm-blue.png',
      '/flappy_elements/farm-purple.png',
      '/game_icons/share.png',
      '/game_icons/like.png'
    ];
    
    imagesToPreload.forEach(src => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  // Game settings
  const PIPE_GAP = 150;
  const PIPE_SPACING = 350;
  const PIPE_WIDTH = 50;
  const BIRD_HEIGHT = 40;

  // Physics constants (memoized to prevent recreation on every render)
  const GRAVITY = useMemo(() => 0.5, []);         // Downward acceleration per frame
  const JUMP_VELOCITY = useMemo(() => -8, []);     // Initial upward velocity on jump
  const MAX_FALL_SPEED = useMemo(() => 5.5, []);     // pixels per frame (adjust for feel)
  const TRAIL_MOVE_SPEED = useMemo(() => 3, []);   // How fast trail dots move left
  const TRAIL_DOT_SIZE = useMemo(() => 10, []);    // Size of each trail dot
  const MAX_TRAIL_LENGTH = useMemo(() => 40, []);
  const MAX_PIPE_HEIGHT_DIFF = useMemo(() => 400, []);
  const skew = useMemo(() => 0.7, []);             // The lower the skew, the more often higher gap height differences will appear

  // Game state
  const [birdY, setBirdY] = useState(window.innerHeight / 2 - BIRD_HEIGHT / 2);
  const [birdVelocity, setBirdVelocity] = useState(0);
  const [pipes, setPipes] = useState([]);
  const [pipeSpeed, setPipeSpeed] = useState(3);
  const [isGameStarted, setIsGameStarted] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const lastPipeTopHeightRef = useRef(null);
  const [pipesPassed, setPipesPassed] = useState(0);
  const [clouds, setClouds] = useState([
    { x: 100, y: 50, width: 80, height: 40 },
    { x: 400, y: 100, width: 120, height: 60 }
  ]);
  const [cloudOpacity, setCloudOpacity] = useState(1);
  const [trailPoints, setTrailPoints] = useState([]);
  const [flap, setFlap] = useState(false);

  const prevBirdYRef = useRef(birdY);
  const { width, height } = useWindowSize();
  const isWide = width > 900 || width > height; // 800 = infinite craft
  const isMobile = window.matchMedia('(pointer: coarse)').matches;
  const [storedGameMode, setStoredGameMode] = useState(null);
  const [lastOrientation, setLastOrientation] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
    isLandscape: width > height
  });

  // Background control
  const [farmBgOffset, setFarmBgOffset] = useState(0);
  const [farmBgImage, setFarmBgImage] = useState('/flappy_elements/farm-green.png');
  const [farmBgOpacity, setFarmBgOpacity] = useState(1);
  const [nextBgOpacity, setNextBgOpacity] = useState(0);
  const [nextBgImage, setNextBgImage] = useState(null);
  const [isBgTransitioning, setIsBgTransitioning] = useState(false);
  const [isBgFadingOut, setIsBgFadingOut] = useState(false);
  const [bgWidth, setBgWidth] = useState(0);
  const [bgHeight, setBgHeight] = useState(0);

  // Scaling background while keeping aspect ratio constant, storing height and width
  const updateBackgroundSize = useCallback(() => {
    const img = new Image();
    img.src = farmBgImage;
    img.onload = () => {
      const originalWidth = img.width;
      const originalHeight = img.height;
      const desiredHeight = height / 4.5;
      const scaleFactor = desiredHeight / originalHeight;
      const scaledWidth = originalWidth * scaleFactor;
      setBgWidth(scaledWidth);
      setBgHeight(desiredHeight);
    };
  }, [farmBgImage, height]);

  // This is how many copies of the background are needed to cover screen width
  const copiesNeeded = useMemo(() => {
    return bgWidth > 0 ? Math.ceil(width / bgWidth) + 1 : 1;
  }, [bgWidth, width]);

  const BG_STAGES = useMemo(() => [
    '#87CEEB', // original
    '#307180', // stage 1
    '#454287', // stage 2
    '#000000', // stage 3 (fully black, before space) // not really used since space mode has its own 'black' as backgroundColor
  ], []);
  const [bgColor, setBgColor] = useState(BG_STAGES[0]);
  const [prevBgColor, setPrevBgColor] = useState(BG_STAGES[0]);
  const [isBgAnimating, setIsBgAnimating] = useState(false);
  const [bgStage, setBgStage] = useState(0);
  const [showSpace, setShowSpace] = useState(false);
  const [isSpaceFadeIn, setIsSpaceFadeIn] = useState(false);
  const [spaceOpacity, setSpaceOpacity] = useState(0);

  // Cutscene stuff
  const [isCutscene, setIsCutscene] = useState(false);
  const [birdRotation, setBirdRotation] = useState(0);
  const [birdCutsceneY, setBirdCutsceneY] = useState(0);
  const [gameOverTextSize, setGameOverTextSize] = useState(0);
  const [gameOverTextOpacity, setGameOverTextOpacity] = useState(0);
  const [cutsceneComplete, setCutsceneComplete] = useState(false);
  const cutsceneCompleteRef = useRef(false); // Ref so that I don't need to include in dependency array and trigger an infinitely repeating cutscene

  // Post-game stuff
  const [leaderboard, setLeaderboard] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const pillRef = useRef(null);
  const [playerName, setPlayerName] = useState('');
  const isNameInvalid =
    !playerName ||
    playerName.length > 6 // Or 30, if you want longer names
  const [scoreSubmitted, setScoreSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [shareError, setShareError] = useState('');
  const [likeCount, setLikeCount] = useState(0);
  const [liked, setLiked] = useState(false);

  // Menu state
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('Controls');
  const [menuLeaderboard, setMenuLeaderboard] = useState([]);
  const [isMenuLeaderboardLoading, setIsMenuLeaderboardLoading] = useState(false);

  // Custom bird state
  const [customBird, setCustomBird] = useState(null); // stores image data URL
  const [useCustomBird, setUseCustomBird] = useState(false);

  // Retrieving any custom birds from localStorage
  useEffect(() => {
    const savedBird = localStorage.getItem('customBird');
    const savedUseFlag = localStorage.getItem('useCustomBird') === 'true';

    if (savedBird && savedUseFlag) {
      setCustomBird(savedBird);
      setUseCustomBird(true);
    }
  }, []);

  // Uploading a new custom bird
  const handleUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = ev => {
        const imageData = ev.target.result;
        setCustomBird(imageData);
        setUseCustomBird(true);
        localStorage.setItem('customBird', imageData);
        localStorage.setItem('useCustomBird', 'true');
      };
      reader.readAsDataURL(file);
    }
  };

  // Resetting custom bird
  const handleReset = () => {
    setCustomBird(null);
    setUseCustomBird(false);
    localStorage.removeItem('customBird');
    localStorage.setItem('useCustomBird', 'false');
  };

  // Helper function for rolling farm background
  const triggerBgTransition = (newImage) => {
    setNextBgImage(newImage);
    setIsBgTransitioning(true);
    setNextBgOpacity(0);
  };

  // Bouncing bird at the beginning
  const [birdBounceY, setBirdBounceY] = useState(0);
  useEffect(() => {
    if (!isGameStarted && !gameOver) {
      let frameId;
      const animate = () => {
        setBirdBounceY(20 * Math.abs(Math.sin(Date.now() / 300)));
        frameId = requestAnimationFrame(animate);
      };
      animate();
      return () => cancelAnimationFrame(frameId);
    }
  }, [isGameStarted, gameOver]);

  // Animation to make text slide in/out and fade in/out at the start screen
  const startFontSize = width < 900 ? Math.min(60, width * 0.12) : 60;
  const [isTextAnimatingOut, setIsTextAnimatingOut] = useState(false);
  const [textOffset, setTextOffset] = useState(0);
  const [textOpacity, setTextOpacity] = useState(1);
  useEffect(() => {
    if (isGameStarted) {
      setTextOffset(width / 2);
      setTextOpacity(0);
      // Optionally, set a timeout to mark animation as done
      const timer = setTimeout(() => setIsTextAnimatingOut(false), 500);
      return () => clearTimeout(timer);
    } else {
      setIsTextAnimatingOut(false);
      setTextOffset(0);
      setTextOpacity(1);
    }
  }, [isGameStarted, width]);

  // Rolling farm background
  useEffect(() => {
    if (!isGameStarted || gameOver || isPaused || isCutscene) return;
  
    let animationId;
    const scrollSpeed = 0.2; // Adjust as needed
  
    const animate = () => {
      setFarmBgOffset(prev => {
        const newOffset = prev - scrollSpeed;
        // Reset when the image is fully scrolled out
        return newOffset <= -bgWidth ? 0 : newOffset;
      });
      animationId = requestAnimationFrame(animate);
    };
    animate();
  
    return () => cancelAnimationFrame(animationId);
  }, [bgWidth, isGameStarted, gameOver, isPaused, isCutscene]);

  // Resizing farm background if windows size changes
  useEffect(() => {
    updateBackgroundSize();
  }, [width, height, farmBgImage, updateBackgroundSize]);

  // Animation for expanding name entry pill once leaderboard loads
  useEffect(() => {
    if (pillRef.current && !isLoading && !scoreSubmitted) {
      pillRef.current.style.animation = 'none';
      void pillRef.current.offsetWidth; // Trigger reflow
      pillRef.current.style.animation = 'expandPill 0.3s cubic-bezier(.4,2,.6,1) forwards';
    }
  }, [isLoading, scoreSubmitted]);

  // Helper: Detect game mode (hdesktop, vdesktop, hmobile, vmobile) - Memoized
  const getGameMode = useCallback(() => {
    const isLandscape = width > 900 || width > height;
    return isMobile
      ? (isLandscape ? 'hmobile' : 'vmobile')
      : (isLandscape ? 'hdesktop' : 'vdesktop');
  }, [width, height, isMobile]);

  // Fetch Mode-Specific Leaderboard and (Total) Likes
  useEffect(() => {
    if (gameOver) {
      setIsLoading(true);
      fetch(`/api/highscores?game=FlappyBird&mode=${storedGameMode}`)
        .then(res => res.json())
        .then(data => {
          setLeaderboard(data);
          setIsLoading(false);
        })
        .catch(error => {
          console.error('Error:', error);
          setIsLoading(false);
        });
  
      fetch('/api/likes?game=FlappyBird')
        .then(res => res.json())
        .then(data => setLikeCount(data.count || 0));
  
      // Check localStorage for like status
      setLiked(localStorage.getItem('flappybird-liked') === 'true');
    }
  }, [gameOver, scoreSubmitted, storedGameMode]);

  // Enhanced handleSubmitScore
  const handleSubmitScore = async () => {
    // Clear any previous error
    setSubmitError('');
    
    // Validate name
    if (!playerName) {
      setSubmitError('Please enter a name.');
      return;
    }
    if (playerName.length > 6) {
      setSubmitError('Name must be 6 characters or less.');
      return;
    }

    setIsSubmitting(true);
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone; // Full timezone, e.g. "America/New_York"
      const mode = getGameMode();
      const response = await fetch('/api/highscores?game=FlappyBird', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: playerName,
          score: Math.floor(pipesPassed),
          game: 'FlappyBird',
          timezone,
          mode
        })
      });

      // Get the response data for both success and error cases
      const data = await response.json();

      if (!response.ok) {
        // If the backend returned a profanity error, show it
        if (data.error && data.error.includes('inappropriate language')) {
          setSubmitError('Profanity detected. Try another name.');
        } else {
          // Otherwise, show a generic error
          setSubmitError(data.error || 'Failed to submit score. Please try again.');
        }
        return;
      }

      // Success!
      setScoreSubmitted(true);
    } catch (e) {
      console.error('Error submitting score:', e);
      setSubmitError('Failed to submit score. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Share Score Handler (Web Share API)
  const handleShare = async () => {
    const shareData = {
      text: `I got ${Math.floor(pipesPassed)} on Flappy Bird. Can you beat me?`,
      url: 'https://reilly.fun/flappy-bird'
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (e) {
        if (e.name !== 'AbortError') {
          setShareError('Failed to share. Please try again.');
        }
      }
    } else {
      setShareError('Sharing not supported on this device.');
    }
  };

  // Like Button Handler
  const handleLikeToggle = async () => {
    const newLiked = !liked;
    setLiked(newLiked);
    localStorage.setItem('flappybird-liked', newLiked);
    // Call backend to increment/decrement like count
    await fetch('/api/likes?game=FlappyBird', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ game: 'FlappyBird', like: newLiked })
    });
    // Refetch like count
    fetch('/api/likes?game=FlappyBird')
      .then(res => res.json())
      .then(data => setLikeCount(data.count || 0));
  };
  

  // Menu handlers
  const handleMenuToggle = () => {
    setIsMenuOpen(!isMenuOpen);
    if (!isMenuOpen && activeTab === 'Leaderboard') {
      // Fetch leaderboard when opening to Leaderboard tab
      fetchMenuLeaderboard();
    }
  };

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    if (tab === 'Leaderboard' && menuLeaderboard.length === 0) {
      fetchMenuLeaderboard();
    }
  };

  const fetchMenuLeaderboard = () => {
    setIsMenuLeaderboardLoading(true);
    const currentMode = getGameMode();
    fetch(`/api/highscores?game=FlappyBird&mode=${currentMode}`)
      .then(res => res.json())
      .then(data => {
        setMenuLeaderboard(data);
        setIsMenuLeaderboardLoading(false);
      })
      .catch(error => {
        console.error('Error:', error);
        setIsMenuLeaderboardLoading(false);
      });
  };

  const handleMenuOutsideClick = (e) => {
    if (e.target.classList.contains('menu-overlay')) {
      setIsMenuOpen(false);
    }
  };

  // Reset and Start
  const resetGame = () => {
    setBirdY(window.innerHeight / 2 - BIRD_HEIGHT / 2);
    setBirdVelocity(0);
    setPipeSpeed(3);
    setPipes([]);
    setPipesPassed(0);
    setBgStage(0);
    setBgColor(BG_STAGES[0]);
    setPrevBgColor(BG_STAGES[0]);
    setIsBgAnimating(false);
    setShowSpace(false);
    setIsSpaceFadeIn(false);
    setSpaceOpacity(0);
    setIsCutscene(false);
    setBirdCutsceneY(window.innerHeight / 2 - BIRD_HEIGHT / 2);
    setBirdRotation(0);
    setGameOverTextSize(0);
    setGameOverTextOpacity(0);
    setCutsceneComplete(false);
    cutsceneCompleteRef.current = false; // reset the ref as well
    setIsGameStarted(false);
    setIsTextAnimatingOut(true);
    setGameOver(false);
    setIsPaused(false);
    setClouds([
      { x: 100, y: 50, width: 80, height: 40 },
      { x: 400, y: 100, width: 120, height: 60 }
    ]);
    setCloudOpacity(1);
    setTrailPoints([]);
    setScoreSubmitted(false);
    setSubmitError('');
    setShareError('');
    lastPipeTopHeightRef.current = null;
    updateBackgroundSize();
    setFarmBgImage('/flappy_elements/farm-green.png');
    setFarmBgOpacity(1);
    setNextBgImage(null);
    setNextBgOpacity(0);
    setIsBgTransitioning(false);
    setIsBgFadingOut(false);
  };

  const startGame = useCallback(() => {
    setStoredGameMode(getGameMode());
    setBirdY(window.innerHeight / 2 - BIRD_HEIGHT / 2);
    setBirdVelocity(0);
    setPipeSpeed(3);
    setPipes([]);
    setPipesPassed(0);
    setBgStage(0);
    setBgColor(BG_STAGES[0]);
    setPrevBgColor(BG_STAGES[0]);
    setIsBgAnimating(false);
    setShowSpace(false);
    setIsSpaceFadeIn(false);
    setSpaceOpacity(0);
    setIsCutscene(false);
    setBirdCutsceneY(window.innerHeight / 2 - BIRD_HEIGHT / 2);
    setBirdRotation(0);
    setGameOverTextSize(0);
    setGameOverTextOpacity(0);
    setCutsceneComplete(false);
    cutsceneCompleteRef.current = false; // reset the ref as well
    setIsGameStarted(true);
    setIsTextAnimatingOut(true);
    setGameOver(false);
    setIsPaused(false);
    setClouds([
      { x: 100, y: 50, width: 80, height: 40 },
      { x: 400, y: 100, width: 120, height: 60 }
    ]);
    setCloudOpacity(1);
    setTrailPoints([]);
    setScoreSubmitted(false);
    setSubmitError('');
    setShareError('');
    lastPipeTopHeightRef.current = null;
    updateBackgroundSize();
    setFarmBgImage('/flappy_elements/farm-green.png');
    setFarmBgOpacity(1);
    setNextBgImage(null);
    setNextBgOpacity(0);
    setIsBgTransitioning(false);
    setIsBgFadingOut(false);
  }, [getGameMode, BG_STAGES, updateBackgroundSize]);

  // Animated Jump
  const animateJump = useCallback(() => {
    if ( gameOver || isPaused) return;

    setBirdVelocity(JUMP_VELOCITY);
    setFlap(true);
    setTimeout(() => setFlap(false), 200);
  }, [gameOver, isPaused, JUMP_VELOCITY]);

  const handleJump = useCallback(() => {
    animateJump();
  }, [animateJump]);

  // Beginning tap should start the game and also jump
  const handleStartAndJump = useCallback(() => {
    startGame();
    handleJump();
  }, [startGame, handleJump]);

  // Keydown handler
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.code === 'Space') {
        if (!isGameStarted && !gameOver) {
          handleStartAndJump();
        } else if (isGameStarted && !gameOver) {
          handleJump();
        }
      }
      if ((e.code === 'KeyP' || e.code === 'Escape') && isGameStarted && !gameOver) {
        setIsPaused(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isGameStarted, gameOver, isPaused, handleJump, handleStartAndJump]);

  // Increase speed by a hair every 20 pipes
  useEffect(() => {
    if (pipesPassed > 0 && pipesPassed % 20 === 0) {
      setPipeSpeed(prev => Math.min(prev + 0.1, 6));
    }
  }, [pipesPassed]);

  // Change background color and farm picture the further the player gets
  useEffect(() => {
    if (pipesPassed >= 33 && bgStage === 0) {
      setPrevBgColor(bgColor);
      setBgStage(1);
      setIsBgAnimating(true);
      setIsBgTransitioning(true);
      triggerBgTransition('/flappy_elements/farm-blue.png');
    } else if (pipesPassed >= 67 && bgStage === 1) {
      setPrevBgColor(bgColor);
      setBgStage(2);
      setIsBgAnimating(true);
      setIsBgTransitioning(true);
      triggerBgTransition('/flappy_elements/farm-purple.png');
    } else if (pipesPassed >= 100 && bgStage === 2) {
      setIsBgTransitioning(true);
      setIsBgFadingOut(true);
      setPrevBgColor(bgColor);
      setBgStage(3);
      setIsBgAnimating(true);
    }
  }, [pipesPassed, bgStage, bgColor]);

  // Background color animation with requestAnimationFrame
  useEffect(() => {
    if (!isBgAnimating) return;
  
    let animationId;
    let startTime;
    // Use a longer duration for the fade to black if needed
    const duration = bgStage === 2 ? 1000 : 700; // 1 second for stage 2 to black, 0.7 for others
  
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);
  
      // Choose the target color based on bgStage
      const targetColor = BG_STAGES[bgStage];
      setBgColor(interpolateColor(prevBgColor, targetColor, progress));
  
      if (progress < 1) {
        animationId = requestAnimationFrame(animate);
      } else {
        setIsBgAnimating(false);
        // Only trigger space mode if we just faded to black
        if (bgStage === 3) {
          setShowSpace(true);
          setIsSpaceFadeIn(true);
        }
      }
    };
  
    animationId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationId);
  }, [isBgAnimating, prevBgColor, BG_STAGES, bgStage]);

  // Fade cloud opacity when stage 2 starts
  useEffect(() => {
    if (bgStage === 2) {
      const duration = 500; // 0.5 seconds
      const startTime = Date.now();
      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        setCloudOpacity(1 - (progress / 2));
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };
      animate();
    }
  }, [bgStage]);

  // Fade out clouds when stage 3 starts
  useEffect(() => {
    if (bgStage === 3) {
      const duration = 500; // 0.5 seconds
      const startTime = Date.now();
      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        setCloudOpacity(0.5 - progress);
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };
      animate();
    }
  }, [bgStage]);

  // Fade in new farm background
  useEffect(() => {
    if (!isBgTransitioning || !nextBgImage) return;
  
    let animationId;
    let startTime;
    const duration = 1000; // 1s
  
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);
      setNextBgOpacity(progress);
      // Add cross-fade by fading out the old image
      setFarmBgOpacity(1 - progress);
  
      if (progress < 1) {
        animationId = requestAnimationFrame(animate);
      } else {
        setFarmBgImage(nextBgImage);
        setFarmBgOpacity(1);
        setNextBgOpacity(0);
        // Set timeouts to mark animation as done
        setTimeout(() => {
          setIsBgTransitioning(false);
          setNextBgImage(null);
        }, 1000);
      }
    };
  
    animationId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationId);
  }, [isBgTransitioning, nextBgImage]);

  // Fade out last farm background
  useEffect(() => {
    if (!isBgFadingOut) return;
  
    let animationId;
    let startTime;
    const duration = 500; // 0.5s
  
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);
      setFarmBgOpacity(1 - progress);
  
      if (progress < 1) {
        animationId = requestAnimationFrame(animate);
      } else {
        // Set a timeout to mark animation as done
        const timer = setTimeout(() => setIsBgFadingOut(false), 500);
        return () => clearTimeout(timer);
      }
    };
  
    animationId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationId);
  }, [isBgFadingOut]);    

  // Fade in space mode elements
  useEffect(() => {
    if (!isSpaceFadeIn) return;
  
    let animationId;
    let startTime;
    const duration = 1000; // 1 second
  
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);
  
      setSpaceOpacity(progress);
  
      if (progress < 1) {
        animationId = requestAnimationFrame(animate);
      } else {
        setIsSpaceFadeIn(false);
      }
    };
  
    animationId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationId);
  }, [isSpaceFadeIn]);

  // Cutscene logic
  const startCutscene = useCallback(() => {
    setIsCutscene(true);
    setBirdCutsceneY(birdY);
    setBirdRotation(0);
    setGameOverTextSize(0);
    setGameOverTextOpacity(0);
    setCutsceneComplete(false);
    cutsceneCompleteRef.current = false; // <-- Reset ref
  
    // Haptic feedback on mobile
    if (isMobile && window.navigator.vibrate) {
      window.navigator.vibrate(100);
    }
  
    const startTime = Date.now();
    const freezeEnd = 500;  // 0.5s
    const rotateEnd = freezeEnd + 500;  // 0.5s
    const fallDistance = window.innerHeight - birdY;
  
    // Start animation loop
    const animateCutscene = () => {
      const elapsed = Date.now() - startTime;
  
      // 1. Freeze phase (0–0.5s)
      if (elapsed < freezeEnd) {
      }
      // 2. Rotate phase (0.5–1s)
      else if (elapsed < rotateEnd) {
        const rotateProgress = (elapsed - freezeEnd) / 500;
        setBirdRotation(90 * rotateProgress);
      }
      // 3. Fall phase (after 1s, quadratic acceleration, until bird is off screen)
      else {
        const fallTime = (elapsed - rotateEnd) / 1000; // Convert elapsed time to seconds
        const fallDuration = fallDistance / 350; // Estimate time to fall at average speed
        const fallProgress = Math.min(Math.pow(fallTime / fallDuration, 2), 1);
        setBirdCutsceneY(birdY + fallDistance * fallProgress);
        if (fallProgress >= 1 && elapsed >= 2000) { // wait for at least one full second after the fall starts to elapse
          setCutsceneComplete(true);
          cutsceneCompleteRef.current = true; // <-- Update ref
          setGameOver(true)
          return;
        }
      }
  
      // Game Over text appears over 1s, then freezes
      if (elapsed < 1000) {
        const textProgress = elapsed / 1000;
        const targetFontSize = width < 900 ? Math.min(60, width * 0.12) : 60; // font size = 9% of viewport width, capped at 60px
        setGameOverTextSize(targetFontSize * textProgress); // 0pt to targetFontSize
        setGameOverTextOpacity(0.5 + 0.5 * textProgress);
      }
  
      if (!cutsceneCompleteRef.current) {
        requestAnimationFrame(animateCutscene);
      }
    };
  
    requestAnimationFrame(animateCutscene);
  }, [birdY, isMobile, width]);

  // Ends game if orientation changes and defaults to horizontal mode for leaderboard fetching
  useEffect(() => {
    const handleResize = () => {
      const newWidth = window.innerWidth;
      const newHeight = window.innerHeight;
      const newIsLandscape = newWidth > newHeight;
      if (isGameStarted && !gameOver) {
        if (
          newIsLandscape !== (lastOrientation.width > lastOrientation.height) ||
          newWidth !== lastOrientation.width ||
          newHeight !== lastOrientation.height
        ) {
          // Orientation changed during game: end the game and force horizontal mode
          const mode = isMobile ? 'hmobile' : 'hdesktop';
          setStoredGameMode(mode);
          startCutscene();
        }
      }
      setLastOrientation({ width: newWidth, height: newHeight, isLandscape: newIsLandscape });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isGameStarted, gameOver, lastOrientation, isMobile, startCutscene]);

  // Ends game if orientation changes on mobile
  useEffect(() => {
    // Define the handler for orientation changes
    const handleOrientationChange = (e) => {
      if (isGameStarted && !gameOver) {
        // Determine the new mode based on whether device is mobile
        const mode = isMobile ? 'hmobile' : 'hdesktop'; // defaults to horizontal (easier)
        setStoredGameMode(mode);
        startCutscene();
      }
    };
  
    // Set up the media query listener
    const mql = window.matchMedia("(orientation: portrait)");
    // Add the listener for future changes
    mql.addEventListener("change", handleOrientationChange);
  
    // Cleanup: remove the listener when the component unmounts
    return () => {
      mql.removeEventListener("change", handleOrientationChange);
    };
  }, [isGameStarted, gameOver, isMobile, startCutscene]);


  // Main game screen generation
  useEffect(() => {
    if (gameOver || !isGameStarted || isPaused || isCutscene) return;

    let animationFrameId;

    const gameLoop = () => {
      // Stop the game from updating physics and checking collisions while the cutscene plays
      if (isCutscene) return;

      // Physics: update velocity and position
      setBirdVelocity(v => {
        const newV = v + GRAVITY;
        return Math.min(newV, MAX_FALL_SPEED);
      });
      setBirdY(y => y + birdVelocity);

      setTrailPoints(prev => {
        // Move all points left and remove off-screen
        const moved = prev
          .map(point => ({ x: point.x - TRAIL_MOVE_SPEED, y: point.y }))
          .filter(point => point.x > -TRAIL_DOT_SIZE);

        // Add the new point
        const updated = [...moved, { x: window.innerWidth / 3, y: birdY }];

        // Limit to MAX_TRAIL_LENGTH
        return updated.length > MAX_TRAIL_LENGTH
          ? updated.slice(updated.length - MAX_TRAIL_LENGTH)
          : updated;
      });

      // Pipes and clouds logic
      setPipes((prev) => {
        let newPipes = prev.map(pipe => ({ ...pipe, x: pipe.x - pipeSpeed }))
          .filter(pipe => pipe.x > -PIPE_WIDTH);

        prev.forEach((pipe) => {
          if (
            pipe.x + PIPE_WIDTH >= window.innerWidth / 3 &&
            pipe.x + PIPE_WIDTH - 3 < window.innerWidth / 3
          ) {
            setPipesPassed(count => count + 1);
          }
        });

        const lastPipeX = newPipes.length > 0
          ? Math.max(...newPipes.map(p => p.x))
          : -Infinity;

        if (lastPipeX <= window.innerWidth - PIPE_SPACING) {
          const minGapFromTop = 50;
          const maxGapFromTop = window.innerHeight - PIPE_GAP - 50;

          let newTopHeight;
          if (lastPipeTopHeightRef.current === null) {
            // First pipe: skewed random between min and max gap height
            const raw = Math.pow(Math.random(), skew); // Skewed toward 0 or 1 depending on skew
            newTopHeight = minGapFromTop + raw * (maxGapFromTop - minGapFromTop);
          } else {
            // Subsequent pipes: apply symmetric skew to delta from previous pipe
            const rand = Math.random() * 2 - 1; // Random between -1 and 1
            const skewedDelta = Math.sign(rand) * Math.pow(Math.abs(rand), skew); // Symmetric skew
            const delta = skewedDelta * MAX_PIPE_HEIGHT_DIFF;

            newTopHeight = lastPipeTopHeightRef.current + delta;

            // Clamp to screen bounds
            newTopHeight = Math.max(minGapFromTop, Math.min(newTopHeight, maxGapFromTop));
          }

          lastPipeTopHeightRef.current = newTopHeight; // Update ref

          newPipes.push({
            x: window.innerWidth,
            topHeight: newTopHeight,
            gap: PIPE_GAP
          });
        }

        return newPipes;
      });

      setClouds(prev => {
        // Move and filter clouds in a single operation
        const movedClouds = prev
          .map(cloud => ({ ...cloud, x: cloud.x - 1 }))
          .filter(cloud => cloud.x > -cloud.width);
        
        // Check if we need to add a new cloud
        const minCloudSpacing = 350;
        const lastCloudX = movedClouds.length > 0 ? Math.max(...movedClouds.map(c => c.x)) : -Infinity;
        
        if (lastCloudX < window.innerWidth - minCloudSpacing) {
          return [
            ...movedClouds,
            {
              x: window.innerWidth,
              y: Math.random() * (window.innerHeight / 3),
              width: 60 + Math.random() * 100,
              height: 30 + Math.random() * 50
            }
          ];
        }
        
        return movedClouds;
      });

      prevBirdYRef.current = birdY;

      // Schedule the next frame
      animationFrameId = requestAnimationFrame(gameLoop);
    };

    animationFrameId = requestAnimationFrame(gameLoop);

    return () => cancelAnimationFrame(animationFrameId);
  }, [
    birdY, gameOver, isGameStarted, isPaused, isCutscene,
    birdVelocity
  ]);

  // Collision detection
  useEffect(() => {
    if (gameOver || !isGameStarted || isPaused) return;

    if (birdY < 0 || birdY > window.innerHeight - BIRD_HEIGHT) {
      startCutscene();
      return;
    }

    pipes.forEach(pipe => {
      if (
        window.innerWidth / 3 + BIRD_HEIGHT > pipe.x &&
        window.innerWidth / 3 < pipe.x + PIPE_WIDTH &&
        (birdY < pipe.topHeight || birdY + BIRD_HEIGHT > pipe.topHeight + PIPE_GAP)
      ) {
        startCutscene();
      }
    });
  }, [birdY, pipes, gameOver, isGameStarted, isPaused, startCutscene]);

  // Pause button
  const handlePauseButtonClick = (e) => {
    e.stopPropagation();
    if (isGameStarted && !gameOver) setIsPaused(prev => !prev);
  };

  // Main Render
  return (
    <>
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: width,
        height: height,
        overflow: 'hidden',
      }}
      {...(isGameStarted ? {} : (!isMenuOpen ? { onClick: handleStartAndJump } : {}))}
      {...(isMobile ? (!isMenuOpen ? { onTouchStart: handleJump } : {}) : (!isMenuOpen ? { onMouseDown: handleJump } : {}))}
      >
        {/* Space background (when active) */}
        {showSpace && (
          <>
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: width,
              height: height,
              backgroundColor: 'black', // keep black
              zIndex: 0
            }} />
            <div style={{ opacity: spaceOpacity, transition: 'opacity 1s', position: 'fixed', top: 0, left: 0, width: width, height: height, zIndex: 1 }}>
              <SpaceBackground />
            </div>
          </>
        )}

        {/* Darkening background (when not in space mode) */}
        {!showSpace && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: width,
            height: height,
            backgroundColor: bgColor,
            zIndex: 0
          }} />
        )}

        {/* Clouds (with changeable opacity in the case of space mode) */}
        {clouds.map((cloud, idx) => (
          <div key={idx} style={{
            position: 'absolute',
            left: `${cloud.x}px`,
            top: `${cloud.y}px`,
            width: `${cloud.width}px`,
            height: `${cloud.height}px`,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            borderRadius: `${cloud.height}px`,
            zIndex: 1,
            opacity: cloudOpacity,
          }} />
        ))}

        {/* Pause Button */}
        {isGameStarted && !gameOver && !isCutscene && (
          <button
            {...(!isMobile ? { onMouseDown: handlePauseButtonClick } : {})}
            {...(isMobile ? { onTouchStart: handlePauseButtonClick } : {})}
            className="pause-button"
            style={{
              position: 'absolute',
              top: 10,
              left: 10,
              zIndex: 20,
              fontSize: '20px',
              padding: '10px 15px',
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              color: '#333',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer'
            }}
          >
            {isPaused ? '▶️' : '⏸️'}
          </button>
        )}

        {/* Menu Button - Bottom Left Corner */}
        {!isGameStarted && !gameOver && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleMenuToggle();
            }}
            style={{
              position: 'absolute',
              bottom: `calc(20px + env(safe-area-inset-bottom, 0px))`,
              left: 20,
              zIndex: 20,
              fontSize: '18px',
              padding: '12px 16px',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              color: '#333',
              border: '2px solid #ddd',
              borderRadius: '8px',
              cursor: 'pointer',
              fontFamily: "'Sniglet', sans-serif",
              fontWeight: 400,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}
          >
            ☰ Menu
          </button>
        )}

        {/* Menu Overlay */}
        {isMenuOpen && (
          <div 
            className="menu-overlay"
            onClick={handleMenuOutsideClick}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: width,
              height: height,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 50,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              minWidth: width * 0.2,
              width: 'auto',
              maxWidth: width * 0.9,
              maxHeight: 'calc(90vh - 100px - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))',
              position: 'relative',
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
              overflow: 'hidden', // Prevents visible negative space in corners
              display: 'flex',
              flexDirection: 'column'
            }}>
              {/* Header with tabs and close button */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '20px 32px 0 32px',
                borderBottom: '1px solid #eee'
              }}>
                <div style={{
                  display: 'flex',
                  gap: '0px',
                  fontFamily: "'Sniglet', sans-serif",
                  fontSize: width < 900 ? Math.min(18, width * 0.03) : 18,
                }}>
                  {['Controls', 'Leaderboard', 'About', '???'].map((tab, index) => (
                    <React.Fragment key={tab}>
                      <span
                        onClick={() => handleTabClick(tab)}
                        style={{
                          cursor: 'pointer',
                          color: activeTab === tab ? 'black' : '#999',
                          fontWeight: 400,
                          transition: 'color 0.2s ease',
                          WebkitTapHighlightColor: 'transparent'
                        }}
                        onMouseEnter={(e) => e.target.style.color = 'black'}
                        onMouseLeave={(e) => e.target.style.color = activeTab === tab ? 'black' : '#999'}
                        onTouchStart={e => e.target.style.color = 'black'}
                        onTouchEnd={e => e.target.style.color = activeTab === tab ? 'black' : '#999'}
                        onTouchCancel={e => e.target.style.color = activeTab === tab ? 'black' : '#999'}
                      >
                        {tab}
                      </span>
                      {index < 3 && <span style={{ color: '#ccc', margin: '0 12px' }}>|</span>}
                    </React.Fragment>
                  ))}
                </div>
                <span
                  onClick={() => setIsMenuOpen(false)}
                  style={{
                    fontSize: '24px',
                    cursor: 'pointer',
                    color: '#999',
                    transition: 'color 0.2s ease',
                    padding: '4px',
                    marginLeft: '32px',
                    WebkitTapHighlightColor: 'transparent'
                  }}
                  onMouseEnter={(e) => e.target.style.color = 'black'}
                  onMouseLeave={(e) => e.target.style.color = '#999'}
                  onTouchStart={e => e.target.style.color = 'black'}
                  onTouchEnd={e => e.target.style.color = '#999'}
                  onTouchCancel={e => e.target.style.color = '#999'}
                >
                  ×
                </span>
              </div>

              {/* Tab Content */}
              <div style={{
                padding: '20px',
                maxHeight: 'calc(90vh - 100px)',
                overflowY: 'auto'
              }}>
                {activeTab === 'Controls' && (
                  <div style={{ fontFamily: "'Sniglet', sans-serif", fontWeight: 400 }}>
                    {!isMobile ? (
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
                          <span style={{ color: 'black' }}>Click, Space</span>
                          <span style={{ color: '#999' }}>Jump</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
                          <span style={{ color: 'black' }}>Esc, p, ⏸️/▶️</span>
                          <span style={{ color: '#999' }}>Pause/Continue</span>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
                          <span style={{ color: 'black' }}>Tap</span>
                          <span style={{ color: '#999' }}>Jump</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
                          <span style={{ color: 'black' }}>⏸️/▶️</span>
                          <span style={{ color: '#999' }}>Pause/Continue</span>
                        </div>
                      </div>
                    )}
                    <button
                      onClick={() => navigate('/')}
                      style={{
                        fontSize: '16px',
                        fontFamily: "'Sniglet', sans-serif",
                        fontWeight: 400,
                        padding: '10px 20px',
                        backgroundColor: '#E3242B',
                        color: 'white',
                        border: 'none',
                        borderRadius: '10px',
                        cursor: 'pointer',
                        width: 'fit-content',
                        margin: '20px auto 0 auto',
                        display: 'block',
                      }}
                    >
                      Quit Game
                    </button>
                  </div>
                )}

                {activeTab === 'Leaderboard' && (
                  <div>
                    {isMenuLeaderboardLoading ? (
                      <div style={{ textAlign: 'center', padding: '40px' }}>
                        <div className="loading-spinner" />
                      </div>
                    ) : (
                      <div style={{ fontFamily: "'Nunito', sans-serif" }}>
                        <div style={{ 
                          display: 'grid', 
                          gridTemplateColumns: '80px 100px 1fr',
                          gap: '12px',
                          fontWeight: 600,
                          borderBottom: '2px solid #ddd',
                          paddingBottom: '8px',
                          marginBottom: '12px'
                        }}>
                          <div style={{ textAlign: 'center' }}>Rank</div>
                          <div style={{ textAlign: 'center' }}>Score</div>
                          <div style={{ textAlign: 'center' }}>Name</div>
                        </div>
                        {menuLeaderboard.map((entry, i) => (
                          <div key={i} style={{ 
                            display: 'grid', 
                            gridTemplateColumns: '80px 100px 1fr',
                            gap: '12px',
                            padding: '8px 0',
                            borderBottom: '1px solid #f0f0f0'
                          }}>
                            <div style={{ textAlign: 'center' }}>{i + 1}.</div>
                            <div style={{ textAlign: 'center' }}>{String(entry.score).padStart(3, '0')}</div>
                            <div style={{ textAlign: 'center' }}>{entry.username}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'About' && (
                  <div style={{
                    fontFamily: "'Sniglet', sans-serif",
                    fontSize: '16px',
                    lineHeight: '1.6',
                    whiteSpace: 'normal',
                    maxWidth: '520px',
                    padding: "10px 20px 20px 20px",
                    overflowY: 'visible',
                  }}>
                    <p>
                      Hi, I'm Reilly! Thanks for flying here.
                      <br />
                      <br />
                      This (very original) Flappy Bird project started as both a fun ragebait game
                      and also my first full-stack app. Hope you enjoy, and please reach out with
                      any more suggestions to <s>infuriate people</s> improve the experience!
                      <br />
                      <br />For more little creations, check out{' '}
                      <a 
                        href="https://reilly.fun" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ color: "#69b3e7", fontFamily: "'Fredoka One', cursive" }}
                      >
                        reilly.fun
                      </a> 🤠
                    </p>
                  </div>
                )}

                {activeTab === '???' && (
                  <div style={{ fontFamily: "'Sniglet', sans-serif", textAlign: 'center', color: '#999', fontSize: '16px', padding: '40px 0' }}>
                    {useCustomBird ? (
                      <>
                        <div style={{ marginBottom: 16, color: 'black' }}>Your bird is live! 🐣 What a goofy guy...  ツ</div>
                        <button onClick={handleReset} style={{
                          fontSize: '14px',
                          fontFamily: "'Sniglet', sans-serif",
                          fontWeight: 400,
                          padding: '10px 20px',
                          backgroundColor: '#6BAED6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '10px',
                          cursor: 'pointer',
                          width: 'fit-content'
                        }}>
                          Use original bird
                        </button>
                      </>
                    ) : (
                      <>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                          <label
                            htmlFor="custom-bird-upload"
                            className="button"
                            style={{
                              background: '#6BAED6',
                              color: 'white',
                              padding: '12px 24px',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              fontFamily: 'inherit',
                              marginBottom: 8
                            }}>
                            Upload your own bird
                          </label>
                          <input
                            id="custom-bird-upload"
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            onChange={handleUpload}
                          />
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Game Over, but wait for cutscene to complete */}
        {cutsceneComplete && (
          <div className="container">
            <span className="outlined-text" style={{ fontSize: 36, fontFamily: "'Sniglet', sans-serif", fontWeight: 800 }}>GAME OVER!</span>
            <br />
            <span style={{ marginBottom: 4, display: 'inline-block', fontFamily: "'Sniglet', sans-serif", fontWeight: 400 }}>
              Final Score: {Math.floor(pipesPassed)}
            </span>

            {isWide ? (
              // LANDSCAPE LAYOUT
              <div style={{
                display: 'flex',
                gap: '20px',
                margin: '0 0 0 0',
                borderRadius: '10px',
                padding: '8px 16px 8px 16px',
                boxSizing: 'border-box',
                width: 'fit-content',
                maxWidth: 'calc(100vw - 40px)',
                marginLeft: 'auto',
                marginRight: 'auto',
                fontFamily: "'Sniglet', sans-serif",
                fontWeight: 400,
              }}>
                {/* Leaderboard on the left */}
                <div
                  style={{
                    background: 'transparent',
                    borderRadius: 12,
                    padding: '0 0 0 0',
                    width: 'fit-content',
                    minWidth: 220,
                    maxWidth: '100%',
                    overflow: 'hidden'
                  }}
                >
                  <div
                    style={{
                      background: '#fff',
                      borderTopLeftRadius: 12,
                      borderTopRightRadius: 12,
                      padding: '8px 8px 8px 8px',
                      textAlign: 'center',
                      fontSize: 24,
                      letterSpacing: 0.5,
                      borderTop: '2px solid #dedede',
                      borderLeft: '2px solid #dedede',
                      borderRight: '2px solid #dedede',
                      borderBottom: '1px solid #dedede',
                      fontFamily: "'Sniglet', sans-serif",
                      fontWeight: 400,
                    }}
                  >
                    LEADERBOARD
                  </div>
                  
                  {/* Loading animation */}
                  {isLoading && (
                    <div className="loading-spinner" style={{
                      padding: 32, background: '#fff',
                      borderLeft: '2px solid #dedede',
                      borderRight: '2px solid #dedede',
                      borderBottom: '2px solid #dedede',
                      borderBottomLeftRadius: 12,
                      borderBottomRightRadius: 12,
                    }} />
                  )}

                  {/* Only show table when finished loading */}
                  {!isLoading && (
                    <div style={{
                      display: 'flex',
                      width: '100%',
                      background: 'transparent',
                      margin: 0,
                      padding: 0,
                      fontFamily: "'Nunito', sans-serif",
                      fontSize: 14,
                      gap: 0,
                      overflow: 'hidden',
                    }}>
                      {/* Left Leaderboard (Ranks 1–3) */}
                      <div style={{
                        flex: 1,
                        background: '#fff',
                        borderLeft: '2px solid #dedede',
                        overflow: 'hidden',
                        padding: '8px 0',
                      }}>
                        {leaderboard.slice(0, 3).map((entry, i) => (
                          <div key={i} style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '4px 0',
                            //borderBottom: i < 2 ? '1px solid #eee' : 'none',
                          }}>
                            <div style={{
                              flex: '0 0 auto',
                              minWidth: 12,
                              textAlign: 'center',
                              fontSize: 16,
                              padding: '0 8px 0 16px',
                            }}>
                              {i === 0 ? '🥇' : i === 1 ? '🥈' : '🥉'}
                            </div>
                            <div style={{
                              flex: '0 0 auto',
                              minWidth: 8,
                              textAlign: 'center',
                              padding: '0 8px 0 8px',
                            }}>
                              {String(entry.score).padStart(3, '0')}
                            </div>
                            <div style={{
                              flex: 1,
                              textAlign: 'center',
                              padding: '0 16px 0 8px',
                            }}>
                              {entry.username}
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Right Leaderboard (Ranks 4–6) */}
                      <div style={{
                        flex: 1,
                        background: '#fff',
                        borderLeft: '1px solid #dedede',
                        borderRight: '2px solid #dedede',
                        overflow: 'hidden',
                        padding: '8px 0',
                      }}>
                        {leaderboard.slice(3, 6).map((entry, i) => (
                          <div key={i} style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '4px 0',
                            //borderBottom: i < 2 ? '1px solid #eee' : 'none',
                          }}>
                            <div style={{
                              flex: '0 0 auto',
                              minWidth: 12,
                              textAlign: 'center',
                              padding: '0 8px 0 16px',
                              fontSize: 16,
                            }}>
                              🏅
                            </div>
                            <div style={{
                              flex: '0 0 auto',
                              minWidth: 8,
                              textAlign: 'center',
                              padding: '0 8px 0 8px',
                            }}>
                              {String(entry.score).padStart(3, '0')}
                            </div>
                            <div style={{
                              flex: 1,
                              textAlign: 'center',
                              padding: '0 16px 0 8px',
                            }}>
                              {entry.username}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Name Entry (Bottom of Leaderboard) */}
                  {!isLoading && !scoreSubmitted && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      background: '#fff',
                      borderBottomLeftRadius: 12,
                      borderBottomRightRadius: 12,
                      padding: '5px 2px',
                      borderTop: '1px solid #dedede',
                      borderLeft: '2px solid #dedede',
                      borderRight: '2px solid #dedede',
                      borderBottom: '2px solid #dedede',
                      width: '100%',
                      boxSizing: 'border-box',
                    }}>
                      <input
                        type="text"
                        placeholder="Add Your Name"
                        value={playerName}
                        onChange={e => setPlayerName(e.target.value.replace(/[^a-zA-Z0-9_]/g, '').slice(0, 6))}
                        maxLength={6}
                        style={{
                          border: 'none',
                          outline: 'none',
                          fontSize: 18,
                          padding: '4px 10px',
                          flex: 1,
                          minWidth: 0,
                          background: 'transparent',
                          fontFamily: "'Sniglet', sans-serif",
                          fontWeight: 400,
                          color: 'black',
                          WebkitTextFillColor: '#000',
                          backgroundColor: '#fff',
                          caretColor: '#000'
                        }}
                        onFocus={e => e.target.placeholder = ''}
                        onBlur={e => e.target.placeholder = 'Add Your Name'}
                      />
                      <button
                        onClick={handleSubmitScore}
                        disabled={isNameInvalid || isSubmitting}
                        style={{
                          marginLeft: 6,
                          marginRight: 3,
                          background: isNameInvalid ? '#ccc' : '#4CAF50',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '10px',
                          padding: '6px 16px',
                          fontSize: 16,
                          cursor: isNameInvalid ? 'not-allowed' : 'pointer',
                          transition: 'background 0.2s',
                          fontFamily: "'Sniglet', sans-serif",
                          fontWeight: 400,
                        }}
                      >
                        Enter
                      </button>
                    </div>
                  )}

                  {/* Error or Success Message */}
                  {(submitError || shareError) ? (
                    <div style={{
                      display: 'flex',
                      justifyContent: 'center',
                      color: 'red',
                      margin: '6px 0',
                      fontSize: '14px',
                      textAlign: 'center',
                      width: '100%',
                      boxSizing: 'border-box',
                    }}>
                      {submitError || shareError}
                    </div>
                  ) : scoreSubmitted ? (
                    <div style={{
                      color: '#28a745',
                      margin: '6px 0',
                      fontSize: '14px',
                      textAlign: 'center',
                      width: '100%',
                      boxSizing: 'border-box',
                    }}>
                      Score submitted to leaderboard!
                    </div>
                  ) : null}

                </div>

                {/* Buttons on the right, stacked */}
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  gap: '16px',
                  width: '120px',
                  minWidth: '120px'
                }}>

                  {/* Play Again button */}
                  <button
                    onClick={resetGame}
                    style={{
                      fontSize: '18px',
                      fontFamily: "'Sniglet', sans-serif",
                      fontWeight: 400,
                      padding: '10px 20px',
                      backgroundColor: '#4CAF50',
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      width: '100%'
                    }}
                  >
                    Play Again
                  </button>

                  {/* Circular Buttons Row */}
                  <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center', minHeight: 24 }}>
                    {/* Share Button */}
                    <button className="circle-icon-btn" onClick={handleShare}>
                      <span className="circle-icon-inner">
                        <img src="/game_icons/share.png" alt="Share" style={{ width: 36, height: 36 }} />
                      </span>
                    </button>
                    {/* Like Button */}
                    <button
                      className="circle-icon-btn"
                      onClick={handleLikeToggle}
                      style={{ 
                        background: '#222'
                      }}
                    >
                      <span className="circle-icon-inner"
                        style={{ 
                          background: liked ? '#FFF9C4' : '#FAF9F6', // Light yellow interior when liked
                          transition: 'all 0.2s ease'
                        }}>
                        <img
                          src="/game_icons/like.png"
                          alt="Like"
                          style={{
                            width: 36,
                            height: 36,
                            filter: liked ? 'none' : 'grayscale(100%)',
                            transition: 'filter 0.2s ease'
                          }}
                        />
                      </span>
                      <span className="like-count"
                        style={{
                          fontFamily: "'Nunito', sans-serif",
                          fontWeight: liked ? 'bold' : 'normal'
                        }}
                      >
                        {likeCount}
                      </span>
                    </button>
                  </div>

                  {/* reilly.fun text */}
                  <div
                    onClick={() => navigate('/')}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                    onTouchStart={() => setIsHovered(true)}
                    onTouchEnd={() => setIsHovered(false)}
                    onTouchCancel={() => setIsHovered(false)}
                    style={{
                      cursor: 'pointer',
                      color: 'black',
                      fontFamily: "'Fredoka One', cursive",
                      fontWeight: 'normal',
                      fontSize: '20px',
                      textAlign: 'center',
                      display: 'block',
                      width: '100%',
                      textShadow: isHovered ? '0 2px 4px rgba(0,0,0,0.2)' : 'none',
                      transition: 'all 0.2s ease-in-out',
                    }}
                  >
                    reilly.fun
                  </div>
                </div>

              </div>

            ) : (
              
              // PORTRAIT/MOBILE LAYOUT
              <div style={{
                maxWidth: 'calc(100vw - 40px)',
                margin: '6px auto',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '16px',
                fontFamily: "'Sniglet', sans-serif",
                fontWeight: 400,
              }}>
                <div
                  style={{
                    background: 'transparent',
                    borderRadius: 12,
                    padding: '0 0 0 0',
                    width: 'fit-content',
                    minWidth: 220,
                    maxWidth: '100%',
                    overflow: 'hidden'
                  }}
                >
                  <div
                    style={{
                      background: '#fff',
                      borderTopLeftRadius: 12,
                      borderTopRightRadius: 12,
                      padding: '8px 8px 8px 8px',
                      textAlign: 'center',
                      fontSize: 24,
                      letterSpacing: 0.5,
                      borderTop: '2px solid #dedede',
                      borderLeft: '2px solid #dedede',
                      borderRight: '2px solid #dedede',
                      borderBottom: '1px solid #dedede',
                      fontFamily: "'Sniglet', sans-serif",
                      fontWeight: 400,
                    }}
                  >
                    LEADERBOARD
                  </div>

                  {/* Loading animation */}
                  {isLoading && (
                    <div className="loading-spinner" style={{
                      padding: 32, background: '#fff',
                      borderLeft: '2px solid #dedede',
                      borderRight: '2px solid #dedede',
                      borderBottom: '2px solid #dedede',
                      borderBottomLeftRadius: 12,
                      borderBottomRightRadius: 12,
                    }} />
                  )}

                  {/* Only show table when finished loading */}
                  {!isLoading && (
                    <div style={{
                      display: 'flex',
                      width: '100%',
                      background: 'transparent',
                      margin: 0,
                      padding: 0,
                      fontFamily: "'Nunito', sans-serif",
                      fontSize: 14,
                      gap: 0,
                      overflow: 'hidden',
                    }}>
                      {/* Left Leaderboard (Ranks 1–3) */}
                      <div style={{
                        flex: 1,
                        background: '#fff',
                        borderLeft: '2px solid #dedede',
                        overflow: 'hidden',
                        padding: '4px 0',
                      }}>
                        {leaderboard.slice(0, 3).map((entry, i) => (
                          <div key={i} style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '4px 0',
                            //borderBottom: i < 2 ? '1px solid #eee' : 'none',
                          }}>
                            <div style={{
                              flex: '0 0 auto',
                              minWidth: 12,
                              textAlign: 'center',
                              fontSize: 16,
                              padding: '0 4px 0 24px',
                            }}>
                              {i === 0 ? '🥇' : i === 1 ? '🥈' : '🥉'}
                            </div>
                            <div style={{
                              flex: '0 0 auto',
                              minWidth: 8,
                              textAlign: 'center',
                              padding: '0 4px 0 4px',
                            }}>
                              {String(entry.score).padStart(3, '0')}
                            </div>
                            <div style={{
                              flex: 1,
                              textAlign: 'center',
                              padding: '0 24px 0 4px',
                            }}>
                              {entry.username}
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Right Leaderboard (Ranks 4–6) */}
                      <div style={{
                        flex: 1,
                        background: '#fff',
                        borderLeft: '2px solid #dedede',
                        borderRight: '2px solid #dedede',
                        overflow: 'hidden',
                        padding: '4px 0',
                      }}>
                        {leaderboard.slice(3, 6).map((entry, i) => (
                          <div key={i} style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '4px 0',
                            //borderBottom: i < 2 ? '1px solid #eee' : 'none',
                          }}>
                            <div style={{
                              flex: '0 0 auto',
                              minWidth: 12,
                              textAlign: 'center',
                              padding: '0 8px 0 16px',
                              fontSize: 16,
                            }}>
                              🏅
                            </div>
                            <div style={{
                              flex: '0 0 auto',
                              minWidth: 8,
                              textAlign: 'center',
                              padding: '0 8px 0 8px',
                            }}>
                              {String(entry.score).padStart(3, '0')}
                            </div>
                            <div style={{
                              flex: 1,
                              textAlign: 'center',
                              padding: '0 16px 0 8px',
                            }}>
                              {entry.username}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Name Entry (Bottom of Leaderboard) */}
                  {!isLoading && !scoreSubmitted && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      background: '#fff',
                      borderBottomLeftRadius: 12,
                      borderBottomRightRadius: 12,
                      padding: '5px 2px',
                      borderTop: '1px solid #dedede',
                      borderLeft: '2px solid #dedede',
                      borderRight: '2px solid #dedede',
                      borderBottom: '2px solid #dedede',
                      width: '100%',
                      boxSizing: 'border-box',
                    }}>
                      <input
                        type="text"
                        placeholder="Add Your Name"
                        value={playerName}
                        onChange={e => setPlayerName(e.target.value.replace(/[^a-zA-Z0-9_]/g, '').slice(0, 6))}
                        maxLength={6}
                        style={{
                          border: 'none',
                          outline: 'none',
                          fontSize: 18,
                          padding: '4px 10px',
                          flex: 1,
                          minWidth: 0,
                          background: 'transparent',
                          fontFamily: "'Sniglet', sans-serif",
                          fontWeight: 400,
                          color: 'black',
                          WebkitTextFillColor: '#000',
                          backgroundColor: '#fff',
                          caretColor: '#000'
                        }}
                        onFocus={e => e.target.placeholder = ''}
                        onBlur={e => e.target.placeholder = 'Add Your Name'} />
                      <button
                        onClick={handleSubmitScore}
                        disabled={isNameInvalid || isSubmitting}
                        style={{
                          marginLeft: 6,
                          marginRight: 3,
                          background: isNameInvalid ? '#ccc' : '#4CAF50',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '10px',
                          padding: '6px 16px',
                          fontSize: 16,
                          cursor: isNameInvalid ? 'not-allowed' : 'pointer',
                          transition: 'background 0.2s',
                          fontFamily: "'Sniglet', sans-serif",
                          fontWeight: 400,
                        }}
                      >
                        Enter
                      </button>
                    </div>
                  )}

                  {/* Error or Success Message */}
                  {(submitError || shareError) ? (
                    <div style={{
                      display: 'flex',
                      justifyContent: 'center',
                      color: 'red',
                      margin: '6px 0',
                      fontSize: '14px',
                      textAlign: 'center',
                      width: '100%',
                      boxSizing: 'border-box',
                    }}>
                      {submitError || shareError}
                    </div>
                  ) : scoreSubmitted ? (
                    <div style={{
                      color: '#28a745',
                      margin: '6px 0',
                      fontSize: '14px',
                      textAlign: 'center',
                      width: '100%',
                      boxSizing: 'border-box',
                    }}>
                      Score submitted to leaderboard!
                    </div>
                  ) : null}

                </div>

                {/* Button row */}
                <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center', minHeight: 24 }}>
                  {/* Share Button */}
                  <button className="circle-icon-btn" onClick={handleShare}>
                    <span className="circle-icon-inner">
                      <img src="/game_icons/share.png" alt="Share" style={{ width: 36, height: 36 }} />
                    </span>
                  </button>
                  {/* Like Button */}
                  <button
                    className="circle-icon-btn"
                    onClick={handleLikeToggle}
                    style={{ 
                      background: '#222'
                    }}
                  >
                    <span className="circle-icon-inner"
                      style={{ 
                        background: liked ? '#FFF9C4' : '#FAF9F6', // Light yellow interior when liked
                        transition: 'all 0.2s ease'
                      }}>
                      <img
                        src="/game_icons/like.png"
                        alt="Like"
                        style={{
                          width: 36,
                          height: 36,
                          filter: liked ? 'none' : 'grayscale(100%)',
                          transition: 'filter 0.2s ease'
                        }}
                      />
                    </span>
                    <span className="like-count"
                      style={{
                        fontFamily: "'Nunito', sans-serif",
                        fontWeight: liked ? 'bold' : 'normal'
                      }}
                    >
                      {likeCount}
                    </span>
                  </button>
                </div>

                {/* Play Again button */}
                <button
                  onClick={resetGame}
                  style={{
                    fontSize: '18px',
                    fontFamily: "'Sniglet', sans-serif",
                    fontWeight: 400,
                    padding: '10px 20px',
                    backgroundColor: '#4CAF50',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    width: 'fit-content'
                  }}
                >
                  Play Again
                </button>

                {/* reilly.fun text */}
                <div
                  onClick={() => navigate('/')}
                  onMouseEnter={() => setIsHovered(true)}
                  onMouseLeave={() => setIsHovered(false)}
                  onTouchStart={() => setIsHovered(true)}
                  onTouchEnd={() => setIsHovered(false)}
                  onTouchCancel={() => setIsHovered(false)}
                  style={{
                    cursor: 'pointer',
                    color: 'black',
                    fontFamily: "'Fredoka One', cursive",
                    fontWeight: 'normal',
                    fontSize: '20px',
                    textAlign: 'center',
                    display: 'block',
                    width: '100%',
                    textShadow: isHovered ? '0 2px 4px rgba(0,0,0,0.2)' : 'none',
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  reilly.fun
                </div>
              </div>
            )}
          </div>
        )}

        {/* Farm Background */}
        {bgWidth > 0 && bgHeight > 0 && farmBgOpacity > 0 && (
          <>
            {Array.from({ length: copiesNeeded }).map((_, i) => (
              <img
                key={i}
                src={farmBgImage}
                alt=""
                style={{
                  position: 'absolute',
                  bottom: 'env(safe-area-inset-bottom, 0px)',
                  left: `${farmBgOffset + i * bgWidth}px`,
                  height: `${bgHeight}px`,
                  width: `${bgWidth}px`,
                  opacity: farmBgOpacity,
                  //transition: 'opacity 0.5s', // already handled by useEffect
                  zIndex: 1,
                  pointerEvents: 'none',
                  userSelect: 'none'
                }}
              />
            ))}
          </>
        )}
        {isBgTransitioning && nextBgImage && nextBgOpacity > 0 && bgWidth > 0 && bgHeight > 0 && (
          <>
            {Array.from({ length: Math.ceil(window.innerWidth / bgWidth) + 1 }).map((_, i) => (
              <img
                key={`next-${i}`}
                src={nextBgImage}
                alt=""
                style={{
                  position: 'absolute',
                  bottom: 'env(safe-area-inset-bottom, 0px)',
                  left: `${farmBgOffset + i * bgWidth}px`,
                  height: `${bgHeight}px`,
                  width: `${bgWidth}px`,
                  opacity: nextBgOpacity,
                  //transition: 'opacity 0.5s', // already handled by useEffect
                  zIndex: 2,
                  pointerEvents: 'none',
                }}
              />
            ))}
          </>
        )}

        {/* Bouncing bird at the Start Screen */}
        {!isGameStarted && !gameOver && (
          <Bird
            yPosition={height / 2 - BIRD_HEIGHT / 2 - birdBounceY}
            xPosition={window.innerWidth / 3}
            rotation={0}
            flap={flap}
            customBird={useCustomBird ? customBird : null}
          />
        )}

        {/* Start Screen text */}
        {isWide ? (

          /* LANDSCAPE LAYOUT */
          (!isGameStarted || isTextAnimatingOut) && (
            <div style={{
              position: 'absolute',
              top: '40%',
              left: '50%',
              transform: `translate(-50%, -50%) translateX(-${textOffset}px)`,
              opacity: textOpacity,
              transition: 'opacity 0.5s, transform 0.5s',
              zIndex: 10,
              pointerEvents: 'auto',
              textAlign: 'center',
              width: '100%'
            }}>
              <WaveText fontSize={`${startFontSize}px`}>
                {useCustomBird ? 'Goofy    Guy' : 'Flappy    Bird'}
              </WaveText>
              <br />
              <br />
              <br />
              <PulseText speed={1}>Tap to Fly</PulseText>
            </div>
          )
        ) : (

          /* PORTRAIT LAYOUT */
          (!isGameStarted || isTextAnimatingOut) && (
            <div style={{
              position: 'absolute',
              top: '40%',
              left: '50%',
              transform: `translate(-50%, -50%) translateX(-${textOffset}px)`,
              opacity: textOpacity,
              transition: 'opacity 0.5s, transform 0.5s',
              zIndex: 10,
              pointerEvents: 'auto',
              textAlign: 'center',
              width: '100%'
            }}>
              <WaveText fontSize={`${startFontSize}px`}>
                {useCustomBird ? 'Goofy    Guy' : 'Flappy    Bird'}
              </WaveText>
              <br />
              <br />
              <br />
              <br />
              <br />
              <PulseText speed={1}>Tap to Fly</PulseText>
            </div>
          )
        )}

        {/* Gameplay elements */}
        {isGameStarted && !gameOver && (
          <>
            {!isCutscene && trailPoints.map((point, i) => (
              <div
                key={i}
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  width: TRAIL_DOT_SIZE + 'px',
                  height: TRAIL_DOT_SIZE + 'px',
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.6)',
                  opacity: Math.pow((i + 1) / trailPoints.length, 4),
                  filter: 'blur(2px)',
                  pointerEvents: 'none',
                  zIndex: 2,
                  transform: `translate(${point.x}px, ${point.y + 10}px)`,
                  willChange: 'transform'
                }}
              />
            ))}
            <Bird
              yPosition={isCutscene ? birdCutsceneY : birdY}
              xPosition={window.innerWidth / 3}
              rotation={isCutscene ? birdRotation : -10}
              flap={!isCutscene && flap}
              customBird={useCustomBird ? customBird : null}
            />
            <Pipes pipes={pipes} />
          </>
        )}

        {/* Cutscene Game Over text */}
        {isCutscene && !cutsceneComplete && (
          <div
            className="outlined-text"
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              fontSize: `${gameOverTextSize}px`,
              fontFamily: "'Sniglet', cursive",
              fontWeight: 800,
              opacity: gameOverTextOpacity,
              color: '#FFFFFF', // White color
              zIndex: 20,
              transition: 'font-size 667ms ease-out, opacity 667ms ease-out', // 0.67 seconds to show
              whiteSpace: 'nowrap' // Ensures the text stays on one line
            }}
          >
            GAME OVER
          </div>
        )}

        {/* Score */}
        {isGameStarted && !gameOver && (
          <div style={{
            position: 'absolute',
            top: 10,
            right: 10,
            color: '#333',
            fontSize: '20px',
            fontFamily: "'Sniglet', sans-serif",
            fontWeight: 400,
            zIndex: 20,
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            padding: '10px 15px',
            borderRadius: '8px'
          }}>
            Score: {Math.floor(pipesPassed)}
          </div>
        )}

        {/* Pause Overlay */}
        {isPaused && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            zIndex: 30,
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            padding: '30px',
            borderRadius: '15px'
          }}>
            <h2 style={{
              fontFamily: "'Sniglet', sans-serif",
              fontWeight: 400,
              fontSize: '40px',
              marginBottom: '20px',
              color: '#000'
              }}
            >
              Paused
            </h2>
            <button
              onClick={() => setIsPaused(false)}
              style={{
                fontSize: '24px',
                fontFamily: "'Sniglet', sans-serif",
                fontWeight: 400,
                padding: '10px 30px',
                cursor: 'pointer',
                backgroundColor: '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                marginBottom: '20px'
              }}
            >
              Continue
            </button>
            <div
              onClick={() => navigate('/')}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              onTouchStart={() => setIsHovered(true)}
              onTouchEnd={() => setIsHovered(false)}
              onTouchCancel={() => setIsHovered(false)}
              style={{
                cursor: 'pointer',
                color: 'black',
                fontFamily: "'Fredoka One', cursive",
                fontWeight: 'normal',
                fontSize: '20px',
                textAlign: 'center',
                display: 'block',
                width: '100%',
                textShadow: isHovered ? '0 2px 4px rgba(0,0,0,0.2)' : 'none',
                transition: 'all 0.2s ease-in-out',
              }}
            >
              reilly.fun
            </div>
          </div>
        )}

      </div>
    </>
  )
}

// SpiralGalaxy component
function SpiralGalaxy({ size = 80, rotation = 0 }) {
  return (
    <svg
      width={size}
      height={size}
      style={{
        position: 'absolute',
        transform: `rotate(${rotation}deg)`,
        pointerEvents: 'none'
      }}
      viewBox="0 0 100 100"
    >
      <g>
        {[...Array(4)].map((_, arm) => (
          <path
            key={arm}
            d={`
              M 50 50
              ${Array.from({ length: 40 }).map((_, i) => {
                const angle = (i / 40) * 2 * Math.PI * 2 + (arm * Math.PI / 2);
                const radius = 5 + i * 1.5;
                const x = 50 + Math.cos(angle) * radius;
                const y = 50 + Math.sin(angle) * radius;
                return `L ${x} ${y}`;
              }).join(' ')}
            `}
            stroke="#fff"
            strokeWidth={1.5}
            fill="none"
            opacity={0.6}
          />
        ))}
        <circle cx="50" cy="50" r="6" fill="#c2aaff" opacity={0.7} />
      </g>
    </svg>
  );
}

// generateSpaceObjects utility
function generateSpaceObjects(diameter) {
  const radius = diameter / 2;
  const cx = radius;
  const cy = radius;

  // Helper to get a random point inside the circle
  function randomPointInCircle() {
    const t = 2 * Math.PI * Math.random();
    const r = radius * Math.sqrt(Math.random());
    return {
      x: cx + r * Math.cos(t),
      y: cy + r * Math.sin(t)
    };
  }

  // Stars
  const stars = Array.from({ length: 1000 }).map(() => {
    const { x, y } = randomPointInCircle();
    return {
      x, y,
      size: Math.random() * 2 + 1
    };
  });

  // Planets
  const planets = Array.from({ length: 10 }).map((_, i) => {
    const { x, y } = randomPointInCircle();
    return {
      x, y,
      size: 20 + Math.random() * 20,
      isBlue: i % 2 === 0,
      baseSlant: Math.random() * 40 - 20
    };
  });

  // Galaxies
  const galaxies = Array.from({ length: 3 }).map(() => {
    const { x, y } = randomPointInCircle();
    return {
      x, y,
      size: 60 + Math.random() * 40,
      rotation: Math.random() * 360
    };
  });

  return { stars, planets, galaxies };
}

function SpaceBackground() {
  // Get the diagonal of the viewport (for the background diameter)
  const [diagonal, setDiagonal] = useState(() => {
    return Math.ceil(Math.sqrt(window.innerWidth ** 2 + window.innerHeight ** 2));
  });
  useEffect(() => {
    const handleResize = () => {
      setDiagonal(Math.ceil(Math.sqrt(window.innerWidth ** 2 + window.innerHeight ** 2)));
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Only generate objects once per mount or diagonal change
  const spaceObjectsRef = useRef(generateSpaceObjects(diagonal * 1.1)); // 10% larger for margin
  useEffect(() => {
    spaceObjectsRef.current = generateSpaceObjects(diagonal * 1.1);
  }, [diagonal]);

  // Space mode animations (now using requestAnimationFrame)
  const [backgroundRotation, setBackgroundRotation] = useState(0);
  const [ringRotation, setRingRotation] = useState(0);
  const animationIdRef = useRef();

  useEffect(() => {
    let lastTime;
    const animate = (timestamp) => {
      if (!lastTime) lastTime = timestamp;
      const delta = timestamp - lastTime;
      lastTime = timestamp;

      // Update background rotation (0.05 deg per frame at 60fps is ~3 deg/s)
      setBackgroundRotation(prev => (prev + (0.05 * delta / 16.67)) % 360); // Adjust for frame timing
      // Update ring rotation (1 deg per frame at 60fps is ~60 deg/s)
      setRingRotation(prev => (prev + (1 * delta / 16.67)) % 360);

      animationIdRef.current = requestAnimationFrame(animate);
    };

    animationIdRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationIdRef.current);
  }, []);

  const { stars, planets, galaxies } = spaceObjectsRef.current;

  return (
    <>
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'black',
        zIndex: 0
      }} />
      <div style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        width: `${diagonal * 1.1}px`,
        height: `${diagonal * 1.1}px`,
        transformOrigin: 'center center',
        transform: `translate(-50%, -50%) rotate(${backgroundRotation}deg)`,
        zIndex: 0,
        pointerEvents: 'none',
        backgroundColor: 'black'
      }}>
        {/* Stars */}
        {stars.map((star, i) => (
          <div
            key={`star-${i}`}
            style={{
              position: 'absolute',
              top: `${star.y}px`,
              left: `${star.x}px`,
              width: `${star.size}px`,
              height: `${star.size}px`,
              background: 'white',
              borderRadius: '50%'
            }}
          />
        ))}
        {/* Planets with rotating rings */}
        {planets.map((p, i) => (
          <div
            key={`planet-${i}`}
            style={{
              position: 'absolute',
              top: `${p.y}px`,
              left: `${p.x}px`,
              width: `${p.size}px`,
              height: `${p.size}px`,
              background: p.isBlue
                ? 'radial-gradient(circle, #00b4d8 60%, #0077b6 100%)'
                : 'radial-gradient(circle, #ffb47b 60%, #ff7b25 100%)',
              borderRadius: '50%'
            }}
          >
            {/* Ring (rotates continuously) */}
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: `translate(-50%, -50%) rotate(${p.baseSlant + ringRotation}deg)`,
                width: `${p.size * 1.5}px`,
                height: `${p.size * 0.45}px`,
                border: '2px solid rgba(255,255,255,0.5)',
                borderRadius: '50%',
                background: 'transparent',
                clipPath: 'polygon(0% 0%, 100% 0%, 100% 50%, 0% 50%)'
              }}
            />
          </div>
        ))}
        {/* Galaxies */}
        {galaxies.map((g, i) => (
          <div
            key={`galaxy-${i}`}
            style={{
              position: 'absolute',
              top: `${g.y}px`,
              left: `${g.x}px`,
              width: `${g.size}px`,
              height: `${g.size}px`,
              pointerEvents: 'none'
            }}
          >
            <SpiralGalaxy size={g.size} rotation={g.rotation} />
          </div>
        ))}
      </div>
    </>
  );
}

export default FlappyBird;